import * as z from 'zod';
import { parsePhoneNumber } from 'libphonenumber-js';
import { dateObj } from '@shared/constants';

export const ageCheck = (age: number) => {
  const dateSchema = z.preprocess(
    (arg) => (typeof arg == 'string' ? new Date(arg) : arg),
    z.date()
  );
  return dateSchema.refine(
    (date) => {
      const ageDiff = dateObj.getFullYear() - date.getFullYear();
      const isBirthdayPassed =
        dateObj.getMonth() > date.getMonth() ||
        (dateObj.getMonth() === date.getMonth() &&
          dateObj.getDate() >= date.getDate());
      return ageDiff > age || (ageDiff === age && isBirthdayPassed);
    },
    {
      message: `You must be at least ${age} years old.`,
    }
  );
};
export const ageCheckWithoutSchema = (age: number, dateField: Date) => {
  const ageDiff = dateObj.getFullYear() - dateField.getFullYear();
  const isBirthdayPassed =
    dateObj.getMonth() > dateField.getMonth() ||
    (dateObj.getMonth() === dateField.getMonth() &&
      dateObj.getDate() >= dateField.getDate());
  return ageDiff > age || (ageDiff === age && isBirthdayPassed);
};

export const zPhoneNumber = z
  .string()
  .min(1, {
    message: 'Required',
  })
  .transform((value, ctx) => {
    const phoneNumber = parsePhoneNumber(value, {
      defaultCountry: 'US',
    });

    if (!phoneNumber?.isValid()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid phone number',
      });
      return z.NEVER;
    }
    return phoneNumber.formatInternational();
  });

export const zPassword = z
  .string()
  .min(8, { message: 'Password must contain at least 8 character(s)' })
  .superRefine((password, checkPassComplexity) => {
    const containsUppercase = (ch: string) => /[A-Z]/.test(ch);
    const containsLowercase = (ch: string) => /[a-z]/.test(ch);
    const containsSpecialChar = (ch: string) =>
      // eslint-disable-next-line no-useless-escape
      /[`!@#$%^&*()_\-+=\[\]{};':"\\|,.<>\/?~ ]/.test(ch);
    let countOfUpperCase = 0,
      countOfLowerCase = 0,
      countOfNumbers = 0,
      countOfSpecialChar = 0;
    for (let i = 0; i < password.length; i++) {
      const ch = password.charAt(i);
      if (!isNaN(+ch)) countOfNumbers++;
      else if (containsUppercase(ch)) countOfUpperCase++;
      else if (containsLowercase(ch)) countOfLowerCase++;
      else if (containsSpecialChar(ch)) countOfSpecialChar++;
    }
    if (
      countOfLowerCase < 1 ||
      countOfUpperCase < 1 ||
      countOfSpecialChar < 1 ||
      countOfNumbers < 1
    ) {
      checkPassComplexity.addIssue({
        code: 'custom',
        message:
          'Password MUST include an 1 UpperCase, 1 LowerCase, special character ',
      });
      return z.NEVER;
    }
    return password;
  });

export const NumberStringConversionSchema = z
  .preprocess((value) => {
    return typeof value === 'number' && value !== 0
      ? String(value)
      : value === ''
        ? '0'
        : value;
  }, z.string())
  .transform((value) => (typeof value === 'string' ? Number(value) : value));

export const DayOfWeekSchema = z
  .preprocess((value) => {
    return typeof value === 'number' ? String(value) : value;
  }, z.string())
  .transform((value) => (typeof value === 'string' ? Number(value) : value));

export const BooleanStrSchema = z
  .preprocess((value) => {
    return typeof value === 'boolean' ? String(value) : value;
  }, z.string())
  .transform((value) =>
    typeof value === 'string' && value === 'true' ? true : false
  );
